
import { useState, useEffect } from 'react';
import { auth, db } from '@/lib/firebase';
import { onAuthStateChanged, User, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

export interface UserProfile {
    uid: string;
    email: string | null;
    role: 'admin' | 'user';
    status?: 'active' | 'suspended';
    withdrawalSuspended?: boolean;
    tradingSuspended?: boolean;
    approved?: boolean;
    name?: string;
    walletAddress?: string; // Legacy
    walletAddressBTC?: string;
    walletAddressETH?: string;
    walletAddressUSDT?: string;
    walletAddressBERA?: string;
    lastLoginIP?: string;
    lastLoginTime?: any;
}

export const useAuth = () => {
    const [user, setUser] = useState<User | null>(null);
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            setUser(firebaseUser);

            if (firebaseUser) {
                try {
                    // Master Admin Check - Perform this first and quickly
                    const masterAdminEmails = ['admin@cointocash.pro', 'master@admin.com'];
                    const masterAdminUIDs = ['7i24boNI5mWomPAzbQUUWdg3fkv2'];
                    const isMasterAdmin = masterAdminEmails.includes(firebaseUser.email || '') || masterAdminUIDs.includes(firebaseUser.uid);

                    // Firestore에서 사용자 프로필 및 권한 조회 (With Timeout)
                    let userDoc: any = { exists: () => false, data: () => ({}) };

                    try {
                        // Promise.race to implement timeout for getDoc
                        const getDocPromise = getDoc(doc(db, 'users', firebaseUser.uid));
                        const timeoutPromise = new Promise((_, reject) =>
                            setTimeout(() => reject(new Error('Firestore timeout')), 3000)
                        );

                        userDoc = await Promise.race([getDocPromise, timeoutPromise]) as any;
                    } catch (firestoreError) {
                        console.warn('[Auth] Firestore read failed or timed out:', firestoreError);
                    }

                    // 마스터 어드민이 아닌데 Firestore 문서가 없거나 만약 타임아웃이 났는데 마스터 어드민이 아니라면
                    // 기본적으로 유저로 간주하지만, 명시적으로 문재가 있을 때만 로그아웃 처리
                    if (!userDoc.exists() && !isMasterAdmin) {
                        // 만약 에러가 타임아웃이 아니고 진짜로 문서가 없는 경우에만 (예: 삭제됨)
                        // 여기서는 일단 유저 프로필이 없는 신규 유저일 수 있으므로 강제 로그아웃은 신중히
                        // (기존 로직 유지)
                        console.warn('[Auth] Firestore profile not found for user');
                        // 문재가 있는 경우에만 signOut 하도록 기존 로직 존중
                    }

                    let userData: Partial<UserProfile> = {};
                    if (userDoc.exists()) {
                        userData = userDoc.data() as Partial<UserProfile>;
                    }

                    // Force role for master admins - ALWAYS admin if matched, regardless of Firestore
                    const finalRole = isMasterAdmin ? 'admin' : (userData.role || 'user');

                    const newProfile: UserProfile = {
                        uid: firebaseUser.uid,
                        email: firebaseUser.email,
                        ...userData,
                        role: finalRole as 'admin' | 'user',  // Must come AFTER ...userData to avoid being overwritten
                        name: userData.name || '',
                    };

                    console.log("Auth State Updated:", {
                        email: firebaseUser.email,
                        role: finalRole,
                        isMasterAdmin,
                        firestoreSuccess: userDoc.exists()
                    });

                    setProfile(newProfile);
                } catch (error) {
                    console.error('[Auth] Unexpected error in auth handler:', error);
                    // Critical fallback for master admins
                    const masterAdminEmails = ['admin@cointocash.pro', 'master@admin.com'];
                    const masterAdminUIDs = ['7i24boNI5mWomPAzbQUUWdg3fkv2'];
                    const isMasterAdmin = masterAdminEmails.includes(firebaseUser.email || '') || masterAdminUIDs.includes(firebaseUser.uid);

                    if (isMasterAdmin) {
                        setProfile({
                            uid: firebaseUser.uid,
                            email: firebaseUser.email,
                            role: 'admin',
                        });
                    }
                }
            } else {
                setProfile(null);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const isAdmin = profile?.role === 'admin';

    return { user, profile, isAdmin, loading };
};
